import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    type Node,
} from "react";
import { History, Location } from "history";
import queryString from "data/queryString";
import { getDisplayName } from "data/constants";

type Props = {
    history: History,
    location: Location,
};

type Config = {
    searchParamName: string,
    namespace: string,
    defaultCurrentRow: number,
    resetOn: ?number,
};

declare function SetCurrentRowFunction(
    rowIndex: number,
    keepHistory?: boolean,
    deleteOtherSearchParams?: Array<string>
): void;

export type TrackCurrentRowProps = {
    currentRow: number,
    setCurrentRow: SetCurrentRowFunction,
};

// will keep record of a current selected row index
// by managing a [marker] in the location.search uri
// giving the current value in [markerProp]
// and providing a function to set it named [markerSet]
const TrackCurrentRow =
    ({
        searchParamName = "tracker",
        namespace = "tracker",
        defaultCurrentRow = -1,
        resetOn = -1,
        parser = (value) => parseInt(value, 10),
    }: Config): Node =>
    (WrappedComponent: ReactClass<any>) => {
        const getCurrentRow = (searchParamValue?: string): number => {
            return searchParamValue !== undefined
                ? parser(searchParamValue)
                : defaultCurrentRow;
        };

        const HOC = ({ location, history, ...props }: Props) => {
            const qs = queryString.parse(location.search);
            const searchParamValue = qs[searchParamName];
            const [currentRow, setCurrentRow] = useState(
                getCurrentRow(searchParamValue)
            );

            useEffect(() => {
                setCurrentRow(getCurrentRow(searchParamValue));
            }, [searchParamValue]);

            const handleSetCurrentRow = useCallback(
                (
                    rowIndex: number,
                    keepHistory?: boolean,
                    deleteOtherSearchParams?: Array<string>
                ): void => {
                    /* istanbul ignore else */
                    if (qs[searchParamName] === String(rowIndex)) return;
                    if (rowIndex === resetOn) {
                        delete qs[searchParamName];
                    } else {
                        qs[searchParamName] = rowIndex;
                    }
                    if (deleteOtherSearchParams !== undefined) {
                        deleteOtherSearchParams.forEach(
                            (searchParam) => delete qs[searchParam]
                        );
                    }

                    if (keepHistory === true) {
                        history.push({
                            search: "?" + queryString.stringify(qs),
                            hash: location.hash,
                        });
                    } else {
                        history.replace({
                            search: "?" + queryString.stringify(qs),
                            hash: location.hash,
                        });
                    }
                },
                [location.hash, qs, history]
            );

            const extraProps = useMemo(() => {
                return {
                    [namespace]: {
                        currentRow,
                        setCurrentRow: handleSetCurrentRow,
                    },
                };
            }, [currentRow, handleSetCurrentRow]);

            return (
                <WrappedComponent
                    history={history}
                    location={location}
                    {...props}
                    {...extraProps}
                />
            );
        };
        HOC.displayName = `TrackCurrentRow(${getDisplayName(
            WrappedComponent
        )})`;
        return HOC;
    };

export default TrackCurrentRow;
